class ScDrawerCurrency extends HTMLElement {
  #abort;
  #observer;
  #source;
  #mirror;
  #signature = '';
  #preferred;
  #applying = false;
  #escapeHandled = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#abort = new AbortController();
    const options = { signal:this.#abort.signal };
    this.#mirror = this.querySelector('select[data-sc-currency]');
    this.addEventListener('change', this.#change, options);
    document.addEventListener('change', this.#sourceChange, options);
    document.addEventListener('BUCKS_SELECTED_CURRENCY', this.#currencySelected, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') this.#escapeHandled = Boolean(this.querySelector('.sc-select__popover:popover-open'));
    }, { ...options, capture:true });
    this.addEventListener('keyup', event => {
      if (event.key === 'Escape' && this.#escapeHandled) { event.stopPropagation(); this.#escapeHandled = false; }
    }, options);
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(document.documentElement, { childList:true, subtree:true });
    document.addEventListener('toggle', () => this.#sync(), { ...options, capture:true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#source = undefined;
    this.#mirror = undefined;
    this.#signature = '';
    this.#preferred = undefined;
    this.#escapeHandled = false;
  }

  #currencySelected = event => {
    if (typeof event.detail !== 'string') return;
    this.#preferred = event.detail;
    this.#sync();
  };

  #sourceChange = event => {
    if (event.target !== this.#source || this.#applying) return;
    this.#preferred = undefined;
    this.#sync();
  };

  #sync() {
    const mirror = this.querySelector('select[data-sc-currency]');
    if (mirror !== this.#mirror) { this.#mirror = mirror; this.#signature = ''; }
    const source = document.querySelector('#bucksSelector');
    this.setAttribute('data-currency-state', !this.#mirror ? 'waiting-control' : source?.tagName !== 'SELECT' ? 'waiting-app' : 'ready');
    if (!this.#mirror || source?.tagName !== 'SELECT') return;
    if (source !== this.#source) {
      this.#source = source;
      this.#observer.disconnect();
      this.#observer.observe(this, { childList:true, subtree:true });
      const sourceOwner = document.querySelector('#sc-currency-source');
      if (sourceOwner) this.#observer.observe(sourceOwner, { childList:true, subtree:true });
      this.#observer.observe(source, { childList:true, subtree:true, attributes:true, attributeFilter:['selected','disabled'] });
    }
    const value = this.#preferred || source.value;
    const signature = JSON.stringify([value,source.disabled,Array.from(source.options,option => [option.value,option.textContent,option.disabled])]);
    const mirrorSignature = JSON.stringify([this.#mirror.value,this.#mirror.disabled,Array.from(this.#mirror.options,option => [option.value,option.textContent,option.disabled])]);
    if (signature === this.#signature && mirrorSignature === signature) return;
    this.#signature = signature;
    const options = Array.from(source.options, option => {
      const copy = new Option(option.textContent, option.value, false, option.value === value);
      copy.disabled = option.disabled;
      return copy;
    });
    this.#mirror.replaceChildren(...options);
    this.#mirror.disabled = source.disabled;
    this.#mirror.value = value;
    this.#mirror.dispatchEvent(new Event('change', { bubbles:true }));
  }

  #change = event => {
    if (event.target !== this.#mirror || this.#applying || !this.#source) return;
    const value = this.#mirror.value;
    if (value === (this.#preferred || this.#source.value)) return;
    if (!Array.from(this.#source.options).some(option => option.value === value && !option.disabled)) return;
    this.#applying = true;
    this.#preferred = value;
    try {
      this.#source.value = value;
      this.#source.dispatchEvent(new Event('change', { bubbles:true }));
    } finally {
      this.#applying = false;
      this.#sync();
    }
  };
}
if (!customElements.get('sc-drawer-currency')) customElements.define('sc-drawer-currency', ScDrawerCurrency);
